import { DataTable } from '@peritasai/ui-components';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid-premium';
import type { SurgeonEntity } from '@peritasai/repository';
import { useTranslation } from 'react-i18next';
import { renderTableCell } from '../shared/renderTableCell';

const getColumns = (
  t: (key: string) => string
): GridColDef<SurgeonEntity>[] => [
  {
    field: 'fullName',
    headerName: t('pages.entityManagement.surgeonsTable.columnFullName'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value, true),
  },
  {
    field: 'npiNumber',
    headerName: t('pages.entityManagement.surgeonsTable.columnNpiNumber'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value),
  },
  {
    field: 'specialty',
    headerName: t('pages.entityManagement.surgeonsTable.columnSpecialty'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value),
  },
  {
    field: 'providerId',
    headerName: t('pages.entityManagement.surgeonsTable.columnProviderId'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value),
  },
  {
    field: 'active',
    headerName: t('pages.entityManagement.surgeonsTable.columnActive'),
    flex: 1,
    headerClassName: 'last-column-header',
    renderCell: (params) =>
      params.value == null
        ? renderTableCell(null)
        : renderTableCell(
            (params.value as boolean)
              ? t('pages.entityManagement.surgeonsTable.activeYes')
              : t('pages.entityManagement.surgeonsTable.activeNo')
          ),
  },
];

interface SurgeonsTableProps {
  rows: SurgeonEntity[];
  rowCount: number;
  isLoading: boolean;
  hasSearch: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onRowClick: (row: SurgeonEntity) => void;
}

export const SurgeonsTable = ({
  rows,
  rowCount,
  isLoading,
  hasSearch,
  paginationModel,
  onPaginationModelChange,
  onRowClick,
}: SurgeonsTableProps) => {
  const { t } = useTranslation(['periverse']);
  const columns = getColumns(t);

  return (
    <DataTable
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      rowCount={rowCount}
      isLoading={isLoading}
      hasSearch={hasSearch}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      onRowClick={onRowClick}
      searchEmptyTitle={t('pages.entityManagement.table.searchEmptyTitle')}
      searchEmptyMessage={t('pages.entityManagement.table.searchEmptyMessage')}
      emptyMessage={t('pages.entityManagement.table.noRows', {
        entityName: t('pages.entityManagement.items.surgeons'),
      })}
      skeletonColumnCount={5}
    />
  );
};
